import React from 'react';
import {
  List,
  TopToolbar,
  CreateButton,
  useRedirect,
  useListContext,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';

const ListActions = () => (
  <TopToolbar>
    <CreateButton
      variant="contained"
      startIcon={<AddIcon />}
    />
  </TopToolbar>
);

const RegionCard = ({ record, onClick }: { record: any; onClick: () => void }) => {
  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
        borderLeft: `4px solid #9c27b0`,
        borderRadius: 0,
        minHeight: 'auto',
        mb: 1,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '400px 250px 1fr 200px',
          gap: 2,
          alignItems: 'center',
          width: '100%'
        }}>
          {/* Column 1: Avatar and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: '#9c27b0',
                width: 48,
                height: 48
              }}
            >
              <LocationIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle1"
                component="h3"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {record.name}
              </Typography>
              {record.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.85rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {record.description}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Column 2: Brand Info */}
          <Box>
            {record.brand?.name && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Brand
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {record.brand.name}
                </Typography>
                {record.brand.enterprise?.name && (
                  <Typography variant="caption" color="text.secondary">
                    {record.brand.enterprise.name}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Column 3: Stats */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {record._count && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Franchisees
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {record._count.tenants || 0}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Column 4: Status */}
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <Chip
              label="Active"
              size="small"
              sx={{
                bgcolor: '#4CAF50',
                color: 'white',
                fontWeight: 500,
                height: 24,
                fontSize: '0.7rem'
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const RegionListHeader = () => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '400px 250px 1fr 200px',
    gap: 2,
    alignItems: 'center',
    px: 2,
    py: 1.5,
    backgroundColor: 'grey.50',
    borderRadius: 0,
    mb: 2,
    border: '1px solid',
    borderColor: 'grey.200'
  }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Region Name
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Brand
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Statistics
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>
      Status
    </Typography>
  </Box>
);

const RegionCardList = () => {
  const { data, isLoading } = useListContext();
  const redirect = useRedirect();

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Box>
      <RegionListHeader />
      {data.map((record: any) => (
        <RegionCard
          key={record.id}
          record={record}
          onClick={() => redirect(`/regions/${record.id}/show`)}
        />
      ))}
    </Box>
  );
};

export const RegionList = () => {
  return (
    <Box>
      <PageTitle
        title="Regions"
        titleKey="pages.regions"
        subtitle="Manage franchise regions"
        icon={<LocationIcon />}
      />
      <List
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'name', order: 'ASC' }}
        empty={<CustomEmptyState resource="regions" />}
        sx={{
          '& .RaList-main': { mt: 0 },
          '& .RaList-content': { p: 2 }
        }}
      >
        <RegionCardList />
      </List>
    </Box>
  );
};
